import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import SEO from "../components/SEO";
import { DEFAULT_SERIES_CARDS } from "../components/SeriesCards";

function orderOf(p) {
    const n = parseFloat(p.bookNumber);
    return isNaN(n) ? 999 : n;
}

function fromPrice(p) {
    const prices = (p.variants && p.variants.length > 0 ? p.variants.map(v => Number(v.price)) : [Number(p.price)])
        .filter(n => n > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
}

function BookTile({ book }) {
    const price = fromPrice(book);
    return (
        <Link
            to={"/book/" + book.id}
            style={{ textDecoration: "none", color: "inherit", display: "block" }}
        >
            <div style={{ aspectRatio: "2 / 3", background: "#eee", borderRadius: 12, overflow: "hidden", boxShadow: "0 6px 18px rgba(0,0,0,0.1)" }}>
                {book.imageUrl && (
                    <img src={book.imageUrl} alt={book.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                )}
            </div>
            <div style={{ marginTop: 12 }}>
                {book.bookNumber && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: 1 }}>
                        Book {book.bookNumber}
                    </span>
                )}
                <p style={{ margin: "4px 0 2px", fontWeight: 700, color: "var(--primary)" }}>{book.title}</p>
                {price !== null && (
                    <p style={{ margin: 0, fontSize: 13, color: "#777" }}>from ${price.toFixed(2)}</p>
                )}
            </div>
        </Link>
    );
}

function Section({ title, books }) {
    if (books.length === 0) return null;
    return (
        <div style={{ marginBottom: 56 }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: "var(--primary)", fontSize: "1.8rem", margin: "0 0 24px" }}>{title}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 28 }}>
                {books.map(b => <BookTile key={b.id} book={b} />)}
            </div>
        </div>
    );
}

export default function SeriesPage({ seriesCards }) {
    const { slug } = useParams();
    const cards = seriesCards && seriesCards.length > 0 ? seriesCards : DEFAULT_SERIES_CARDS;
    const card = cards.find(c => c.slug === slug);
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!card) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        getDocs(collection(db, "products"))
            .then(snap => {
                if (cancelled) return;
                const list = [];
                snap.forEach(d => {
                    const p = d.data();
                    if ((card.seriesNames || []).includes(p.series)) list.push({ id: d.id, ...p });
                });
                setBooks(list);
                setLoading(false);
            })
            .catch(e => {
                console.error("Error loading series:", e);
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [slug, seriesCards]);

    if (loading) return (
        <div style={{ padding: "120px 0", textAlign: "center", color: "#888" }}>Loading series…</div>
    );

    if (!card) return (
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
            <h2>Series not found</h2>
            <Link to="/" style={{ color: "var(--accent)" }}>Back to Home</Link>
        </div>
    );

    const sorted = books.slice().sort((a, b) => orderOf(a) - orderOf(b));
    const english = sorted.filter(b => b.language !== "Spanish");
    const spanish = sorted.filter(b => b.language === "Spanish");

    return (
        <div>
            <SEO
                pageId="shop"
                manualData={{ title: card.title + " | LB Books", description: card.description || card.title, image: card.imageUrl }}
            />

            {/* Hero */}
            <div style={{ background: "linear-gradient(135deg, #1a6b4a 0%, #0d4a35 100%)", padding: "80px 20px 60px", color: "#fff" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    <Link to="/" style={{ color: "#aaa", textDecoration: "none", fontSize: 14 }}>← Back to home</Link>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "3rem", margin: "24px 0 16px", color: "#fff" }}>{card.title}</h1>
                    {card.description && (
                        <p style={{ maxWidth: 720, fontSize: "1.1rem", lineHeight: 1.7, color: "#ccc", whiteSpace: "pre-line", margin: 0 }}>
                            {card.description}
                        </p>
                    )}
                </div>
            </div>

            {/* Books */}
            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "64px 20px" }}>
                {sorted.length === 0 && (
                    <p style={{ color: "#888", textAlign: "center" }}>No books in this series yet.</p>
                )}
                <Section title="English Editions" books={english} />
                <Section title="Ediciones en Español" books={spanish} />
            </div>
        </div>
    );
}
